import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Ticket, Loader2, CreditCard, ChevronUp, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { LegalConsent } from "@/components/legal-consent";
import { useI18n } from "@/lib/i18n";
import { useAutoTranslate } from "@/lib/auto-translate";

export const Route = createFileRoute("/events/$eventId/checkout/$ticketTypeId")({
  head: () => ({ meta: [{ title: "Ödeme — JUËN" }] }),
  component: CheckoutPage,
});

interface EventRow {
  id: string;
  name: string;
  starts_at: string;
  venue: string | null;
  vat_percent: number | string | null;
  service_fee_kurus: number | null;
  min_age: number | null;
}

interface TicketTypeRow {
  id: string;
  event_id: string;
  name: string;
  description: string | null;
  price_kurus: number;
  quantity: number;
  shopier_payment_url: string | null;
}


function CheckoutPage() {
  const { eventId, ticketTypeId } = Route.useParams();
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [event, setEvent] = useState<EventRow | null>(null);
  const [ticket, setTicket] = useState<TicketTypeRow | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [pageLoading, setPageLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<"select" | "pay">("pay");
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const tc = useAutoTranslate([event?.name, ticket?.name, ticket?.description]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
    try {
      const stored = sessionStorage.getItem(`ticket-qty:${ticketTypeId}`);
      if (stored) {
        const n = parseInt(stored, 10);
        if (Number.isFinite(n) && n >= 1) setQuantity(n);
      }
    } catch {}
    console.log("[Checkout] mounted with params", { eventId, ticketTypeId, path: window.location.pathname });
  }, [eventId, ticketTypeId]);

  useEffect(() => {
    if (ticket && ticket.id !== ticketTypeId) {
      console.warn("[Checkout] ticketTypeId mismatch", { urlTicketTypeId: ticketTypeId, loadedTicketId: ticket.id });
    } else if (ticket) {
      console.log("[Checkout] ticket loaded OK", { ticketTypeId, name: ticket.name, eventId: ticket.event_id });
    }
  }, [ticket, ticketTypeId]);

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    void (async () => {
      const [{ data: ev }, { data: tk }] = await Promise.all([
        supabase.from("events").select("id,name,starts_at,venue,vat_percent,service_fee_kurus,min_age").eq("id", eventId).maybeSingle(),
        supabase.from("ticket_types").select("*").eq("id", ticketTypeId).maybeSingle(),
      ]);
      setEvent(ev as EventRow | null);
      setTicket(tk as TicketTypeRow | null);
      setPageLoading(false);
    })();
  }, [eventId, ticketTypeId, user]);

  if (loading || !user || pageLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  if (!event || !ticket || ticket.event_id !== eventId) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <h1 className="text-2xl font-bold">{t("events.ticketNotFoundTitle")}</h1>
          <Button asChild className="mt-4">
            <Link to="/events/$eventId" params={{ eventId }}>{t("events.backToEvent")}</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (profile?.membership_status !== "approved") {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <h1 className="text-2xl font-bold">{t("events.approvedMembershipRequired")}</h1>
          <p className="mt-2 text-muted-foreground">{t("events.approvedMembershipForTicket")}</p>
        </div>
      </div>
    );
  }

  const minAge = event.min_age ?? 0;
  const userAge = (() => {
    const bd = profile?.birth_date;
    if (!bd) return null;
    const d = new Date(bd);
    if (isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age;
  })();
  if (minAge > 0 && userAge !== null && userAge < minAge) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <h1 className="text-2xl font-bold">{t("events.ageLimitTitle", { minAge })}</h1>
          <p className="mt-2 text-muted-foreground">{t("events.ageLimitForTicket")}</p>
          <Button asChild className="mt-4">
            <Link to="/events/$eventId" params={{ eventId }}>{t("events.backToEvent")}</Link>
          </Button>
        </div>
      </div>
    );
  }

  const maxQty = ticket.quantity;
  const ticketTotalKurus = ticket.price_kurus * quantity;
  const vatPercent = Math.max(0, Number(event.vat_percent ?? 0));
  const vatKurus = Math.round(ticketTotalKurus * (vatPercent / 100));
  const serviceFeeKurus = Math.max(0, event.service_fee_kurus ?? 0) * quantity;
  const feeKurus = vatKurus + serviceFeeKurus;
  const totalKurus = ticketTotalKurus + feeKurus;

  const handleCheckout = async () => {
    if (!user || !ticket) return;
    if (!legalAccepted) {
      toast.error(t("events.legalConsentRequired"));
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.rpc("create_ticket_order", {
        _event_id: eventId,
        _ticket_type_id: ticketTypeId,
        _quantity: quantity,
        _drinks: [],
      });
      if (error) throw error;

      const orderId = (data as { order_id?: string })?.order_id;
      if (!orderId) throw new Error(t("events.orderCreateFailed"));

      if (!ticket.shopier_payment_url) {
        toast.error(t("events.shopierUrlMissing"));
        setSubmitting(false);
        return;
      }

      void navigate({
        to: "/orders/$orderId/shopier",
        params: { orderId },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : t("events.orderCreateFailed");
      toast.error(message);
      setSubmitting(false);
    }
  };

  if (step === "pay") {
    return (
      <div className="min-h-screen bg-muted/40">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-8">
          <div className="mb-6 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep("select")}
              className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:opacity-80"
            >
              <ArrowLeft className="h-4 w-4" /> {t("events.back")}
            </button>
            <h1 className="text-lg font-bold tracking-tight">
              {t("events.paymentStep")} <span className="text-muted-foreground font-normal">4 / 4</span>
            </h1>
            <div className="w-12" />
          </div>

          {/* Payment method card */}
          <div className="rounded-2xl bg-card shadow-card overflow-hidden">
            <div className="flex items-center gap-3 bg-muted/50 px-5 py-4">
              <div className="flex h-8 w-10 items-center justify-center rounded bg-background">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
              </div>
              <span className="text-base font-semibold">Shopier</span>
            </div>
            <div className="px-5 py-4 text-center text-sm text-muted-foreground">
              {t("events.ticketsInAccount")}
            </div>
          </div>

          {/* Order summary card */}
          <div className="mt-5 rounded-2xl bg-card shadow-card overflow-hidden">
            <button
              type="button"
              onClick={() => setSummaryOpen((v) => !v)}
              className="flex w-full items-start justify-between px-5 py-5 text-left"
            >
              <div>
                <div className="text-lg font-bold">{t("events.oneEvent")}</div>
                <div className="text-sm text-muted-foreground">{t("events.ticketCount", { count: quantity })}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">{t("events.total")}</div>
                <div className="flex items-center justify-end gap-1 text-lg font-bold">
                  TRY {(totalKurus / 100).toFixed(2)}
                  {summaryOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
              </div>
            </button>

            {summaryOpen && (
              <div className="border-t border-border px-5 py-5">
                <div className="flex gap-4">
                  <div className="flex h-16 w-16 flex-col items-center justify-center rounded-md bg-foreground text-background">
                    <div className="text-[10px] font-semibold tracking-wide">
                      {new Date(event.starts_at).toLocaleDateString(locale, { month: "short" }).toUpperCase()}
                    </div>
                    <div className="text-2xl font-bold leading-none">
                      {new Date(event.starts_at).getDate()}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold leading-tight">{tc(event.name)}</div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {new Date(event.starts_at).toLocaleString(locale, { dateStyle: "short", timeStyle: "short" })}
                    </div>
                  </div>
                </div>

                <div className="mt-5 space-y-4">
                  <div className="flex items-start justify-between gap-3 border-t border-border pt-4">
                    <div>
                      <div className="font-semibold">{tc(ticket.name)}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        TRY {(ticket.price_kurus / 100).toFixed(2)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">{quantity} x TRY {(ticket.price_kurus / 100).toFixed(2)}</div>
                      <div className="font-bold">TRY {(ticketTotalKurus / 100).toFixed(2)}</div>
                    </div>
                  </div>

                </div>

                <div className="mt-5 space-y-1.5 border-t border-border pt-4 text-sm">
                  <div className="flex justify-end gap-8">
                    <span className="text-muted-foreground">{t("events.subtotal")}</span>
                    <span className="w-24 text-right">TRY {(ticketTotalKurus / 100).toFixed(2)}</span>
                  </div>
                  {vatKurus > 0 && (
                    <div className="flex justify-end gap-8">
                      <span className="text-muted-foreground">{t("events.vat", { percent: vatPercent })}</span>
                      <span className="w-24 text-right">TRY {(vatKurus / 100).toFixed(2)}</span>
                    </div>
                  )}
                  {serviceFeeKurus > 0 && (
                    <div className="flex justify-end gap-8">
                      <span className="text-muted-foreground">{t("events.serviceFee")}</span>
                      <span className="w-24 text-right">TRY {(serviceFeeKurus / 100).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-end gap-8 pt-2">
                    <span className="font-semibold">{t("events.total")}</span>
                    <span className="w-24 text-right text-xl font-bold">TRY {(totalKurus / 100).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Place order button */}
          <div className="mt-6 space-y-3 rounded-2xl bg-card p-4 shadow-card">
            <LegalConsent checked={legalAccepted} onCheckedChange={setLegalAccepted} id="legal-pay" />
            <Button
              onClick={handleCheckout}
              disabled={submitting || !legalAccepted}
              size="lg"
              className="h-14 w-full rounded-full bg-primary text-base font-semibold hover:bg-primary/90"
            >
              {submitting ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  {t("events.confirmAndPay")} <ChevronRight className="ml-1 h-5 w-5" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-10">
        <Link
          to="/events/$eventId"
          params={{ eventId }}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> {t("events.backToEvent")}
        </Link>

        <h1 className="text-3xl font-bold tracking-tight">{t("events.paymentStep")}</h1>
        <p className="mt-2 text-muted-foreground">{tc(event.name)}</p>

        <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="flex items-center gap-2">
            <Ticket className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">{tc(ticket.name)}</h2>
          </div>
          {ticket.description && <p className="mt-1 text-sm text-muted-foreground">{tc(ticket.description)}</p>}

          <div className="mt-6 space-y-2">
            <Label htmlFor="qty">{t("events.quantity")}</Label>
            <Input
              id="qty"
              type="number"
              min={1}
              max={maxQty}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, Math.min(maxQty, Number(e.target.value) || 1)))}
            />
            <p className="text-xs text-muted-foreground">{t("events.stockLeft", { count: maxQty })}</p>
          </div>
        </div>

        {/* Summary */}
        <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("events.ticketLine", { count: quantity, name: tc(ticket.name) })}</span>
              <span>{(ticketTotalKurus / 100).toLocaleString(locale)} ₺</span>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
            <span className="text-sm text-muted-foreground">{t("events.total")}</span>
            <span className="text-2xl font-bold">{(totalKurus / 100).toLocaleString(locale)} ₺</span>
          </div>

          <Button onClick={() => setStep("pay")} disabled={ticket.quantity === 0} className="mt-6 w-full" size="lg">
            {t("events.proceedToPay")}
          </Button>
          <p className="mt-3 text-center text-xs text-muted-foreground">
            {t("events.securePaymentNotice")}
          </p>
        </div>
      </div>
    </div>
  );
}
